<template>
  <div class="h-dvh flex flex-col bg-pitch-black text-total-white">
    <TopNavbar />
    <!-- Header -->
    <header class="flex justify-between items-center bg-dark-grey p-4">
      <h1 class="text-xl font-semibold">New Instructions</h1>
      <div class="flex items-center text-sm gap-2">
        <img
          v-if="isCreating"
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-8 h-8"
        />
       
      </div>
    </header>
    <div class="overflow-y-auto py-4">
      <!-- Instructions Form -->
      <form
        class="flex flex-col space-y-4 px-4 h-[80dvh]"
        @submit.prevent="submitForm"
        :class="{ 'opacity-50': isCreating }"
      >
        <!-- Instructions Name -->
        <div class="flex flex-col">
          <label for="instructions-name" class="text-sm font-medium w-fit">
            Instructions Name *
          </label>
          <input
            id="instructions-name"
            type="text"
            v-model="instructionName"
            @input="errors.instructionName = ''"
            class="mt-2 block w-full bg-dark-grey border border-1 border-total-white-100 text-total-white px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-prime-golden"
          />
          <!-- Instructions Name Error Message -->
          <div
            v-if="errors.instructionName"
            class="text-deep-rust text-sm text-left mt-1"
          >
            {{ errors.instructionName }}
          </div>
        </div>

        <!-- Instructions Textarea -->
        <div class="flex-col flex flex-1 h-full">
          <label for="instructions" class="text-sm w-fit font-medium text-total-white">
            Instructions *
          </label>
          <textarea
            id="instructions"
            v-model="instructionContent"
            @input="errors.instructionContent = ''"
            rows="8"
            class="flex-1 mt-2 block w-full bg-dark-grey border border-1 border-total-white-100 text-total-white px-3 py-3 rounded-lg outline-none focus:ring-2 focus:ring-prime-golden resize-none h-full"
          ></textarea>
          <!-- Instructions Content Error Message -->
          <div
            v-if="errors.instructionContent"
            class="text-deep-rust text-sm text-left mt-1"
          >
            {{ errors.instructionContent }}
          </div>
        </div>

        <!-- Is Default Checkbox -->
        <div class="flex items-center gap-2 mt-4">
          <input type="checkbox" id="is_default" v-model="isDefault" class="hidden" />
          <div
            @click="isDefault = !isDefault"
            :class="[
              'w-5 h-5 rounded flex items-center justify-center border cursor-pointer',
              isDefault ? 'bg-dark-grey border-gray-700' : 'bg-gray-600 border-gray-700',
            ]"
          >
            <svg
              v-if="isDefault"
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-prime-golden"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="3"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <label for="is_default" class="ml-1 text-sm font-medium">is default</label>
        </div>
        <div class="flex items-center text-sm gap-2 mt-6 justify-end">
         <!-- Create Button -->
         <button
          class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          @click="submitForm"
          :disabled="isCreating"
        >
          Create
        </button>
        <!-- Cancel Button -->
        <button
          class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg hover:opacity-90"
          @click="cancelForm"
          :disabled="isCreating"
        >
          Cancel
        </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import { useInstructionStore } from "@/stores/instruction";

const router = useRouter();
const route = useRoute();
const instructionStore = useInstructionStore();

const instructionName = ref("");
const instructionContent = ref("");
const isDefault = ref(false);
const isCreating = ref(false);
const errors = ref({});

onMounted(async () => {
  const instructionId = route.query.id;
  if (instructionId) {
    const instruction = await instructionStore.fetchInstructionById(instructionId);
    if (instruction) {
      instructionName.value = instruction.name + " - Fork";
      instructionContent.value = instruction.content;
      isDefault.value = instruction.is_default || false;
    }
  }
});

// Submit the form
const submitForm = async () => {
  errors.value = {};
  if (!instructionName.value) {
    errors.value.instructionName = "Instructions Name is required.";
  }
  if (!instructionContent.value) {
    errors.value.instructionContent = "Instructions content is required.";
  }
  if (Object.keys(errors.value).length > 0) {
    return;
  }

  try {
    isCreating.value = true;
    const instructionData = {
      name: instructionName.value,
      content: instructionContent.value,
      is_default: isDefault.value,
    };
    await instructionStore.createInstruction(instructionData);
    await instructionStore.fetchInstructions();
    router.push({
      name: "ViewInstructionPage",
      params: {
        id: instructionStore.instructions[instructionStore.instructions.length - 1].id,
      },
    });
  } catch (error) {
    console.error("Error submitting form:", error);
  } finally {
    isCreating.value = false;
  }
};

const cancelForm = () => {
  router.push({ name: "InstructionsPage" });
};
</script>
